import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Check, Eye, EyeOff, Search } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { colors } from '../theme/colors';
import { useScrollLock } from '../hooks/useScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
`;

const ColumnList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: ${props => props.visible ? '#f0f9ff' : '#f9fafb'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.visible ? '#e0f2fe' : '#f3f4f6'};
  }
`;

const ColumnInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ColumnName = styled.span`
  font-weight: 500;
  color: #374151;
`;

const ColumnType = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const ToggleButton = styled.button`
  background: ${props => props.visible ? '#4a4a4a' : '#e5e7eb'};
  border: 1px solid ${props => props.visible ? 'rgba(255, 255, 255, 0.1)' : '#d1d5db'};
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.visible ? '#e0e0e0' : '#6b7280'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.visible ? '#555' : '#d1d5db'};
  }
`;


const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: ${colors.primary};
    color: white;
    border: 1px solid ${colors.primary};
    
    &:hover {
      background: ${colors.primaryHover};
      border-color: ${colors.primaryHover};
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const ColumnVisibilityModal = ({ isOpen, onClose, columns, visibleColumns, onToggleColumn, onSave }) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  
  const [localVisibleColumns, setLocalVisibleColumns] = useState(visibleColumns);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Default all columns to visible if not set
      const defaultVisible = {};
      columns.forEach(col => {
        defaultVisible[col.key] = visibleColumns[col.key] !== false; // Default to true unless explicitly false
      });
      setLocalVisibleColumns(defaultVisible);
    }
  }, [isOpen, visibleColumns, columns]);

  // Filter columns based on search query
  const filteredColumns = columns.filter(column =>
    column.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    column.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (columnKey) => {
    setLocalVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleSave = () => {
    onSave(localVisibleColumns);
    onClose();
  };

  const handleSelectAll = () => {
    setLocalVisibleColumns(prev => {
      const updated = { ...prev };
      filteredColumns.forEach(col => {
        updated[col.key] = true;
      });
      return updated;
    });
  };

  const handleSelectNone = () => {
    setLocalVisibleColumns(prev => {
      const updated = { ...prev };
      filteredColumns.forEach(col => {
        updated[col.key] = false;
      });
      return updated;
    });
  };

  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Column Visibility</ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <SearchContainer>
              <SearchIcon>
                <Search size={16} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
              <Button onClick={handleSelectAll}>Select All</Button>
              <Button onClick={handleSelectNone}>Select None</Button>
            </div>
            
            <ColumnList>
              {filteredColumns.map((column) => (
                <ColumnItem key={column.key} visible={localVisibleColumns[column.key]}>
                  <ColumnInfo>
                    <ColumnName>{column.title}</ColumnName>
                    <ColumnType>{column.key === 'loan_id' ? 'ID' : column.key === 'actions' ? 'Action' : 'Data'}</ColumnType>
                  </ColumnInfo>
                  <ToggleButton
                    visible={localVisibleColumns[column.key]}
                    onClick={() => handleToggle(column.key)}
                  >
                    {localVisibleColumns[column.key] ? <Eye size={16} /> : <EyeOff size={16} />}
                  </ToggleButton>
                </ColumnItem>
              ))}
            </ColumnList>
            
            {filteredColumns.length === 0 && searchQuery && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                No columns found matching "{searchQuery}"
              </div>
            )}
          </ModalBody>
          
          <ModalFooter>
            <Button onClick={onClose}>Cancel</Button>
            <Button primary onClick={handleSave}>Save Changes</Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default ColumnVisibilityModal;
